/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import AuthClient from 'fxa-auth-client/browser';
import { ERRNO } from '@fxa/accounts/errors';
import {
  sessionToken,
  clearSignedInAccountUid,
  currentAccount,
} from '../lib/cache';
import {
  isSignedIn as checkIsSignedIn,
  getSessionVerified,
  setSessionVerified,
  updateAccountData,
  dispatchStorageEvent,
} from '../lib/account-storage';

export interface SessionData {
  verified: boolean | null;
  token: string | null;
  verifySession?: (
    code: string,
    options: {
      service?: string;
      scopes?: string[];
      marketingOptIn?: boolean;
      newsletters?: string[];
    }
  ) => Promise<void>;
  destroy?: () => void;
}

/**
 * Check if user is signed in (derived from account storage)
 */
export function getStoredSignedInStatus(): boolean {
  return checkIsSignedIn();
}

/**
 * Mark session as verified in account storage
 * Note: "signed in" status is derived from having a sessionToken,
 * so this function primarily marks the session as verified.
 */
export function setStoredSignedInStatus(isSignedIn: boolean): void {
  if (isSignedIn) {
    setSessionVerified(true);
  }
  dispatchStorageEvent('isSignedIn');
}

export class Session implements SessionData {
  private readonly authClient: AuthClient;
  private _loading: boolean;

  constructor(authClient: AuthClient) {
    this.authClient = authClient;
    this._loading = false;
  }

  private async withLoadingStatus<T>(promise: Promise<T>) {
    this._loading = true;
    try {
      return await promise;
    } finally {
      this._loading = false;
    }
  }

  get token(): string {
    return sessionToken() || '';
  }

  get verified(): boolean {
    return getSessionVerified();
  }

  async verifySession(
    code: string,
    options: {
      service?: string;
      scopes?: string[];
      marketingOptIn?: boolean;
      newsletters?: string[];
    } = {}
  ) {
    await this.withLoadingStatus(
      this.authClient.sessionVerifyCode(sessionToken()!, code, options)
    );
    setSessionVerified(true);
    updateAccountData({ verified: true });
    setStoredSignedInStatus(true);
  }

  async sendVerificationCode() {
    await this.withLoadingStatus(
      this.authClient.sessionResendVerifyCode(sessionToken()!)
    );
  }

  async destroy(browserSessionToken?: string) {
    const token = sessionToken();
    if (token) {
      try {
        await this.authClient.sessionDestroy(token);
      } catch (e) {
        // Token already invalid on the server:
        // treat as already-destroyed so local cleanup + redirect still run.
        if ((e as { errno?: number })?.errno !== ERRNO.INVALID_TOKEN) {
          throw e;
        }
      }
    }
    if (browserSessionToken) {
      // Best effort: a failure here must not block local sign-out.
      await this.authClient.sessionDestroy(browserSessionToken).catch(() => {});
    }
    clearSignedInAccountUid();
    dispatchStorageEvent('isSignedIn');
  }

  get isDestroyed() {
    return currentAccount() == null;
  }

  async isSessionVerified(): Promise<boolean> {
    const token = sessionToken();
    if (!token) {
      return false;
    }

    try {
      const status = await this.authClient.sessionStatus(token);
      const verified = status.state === 'verified';
      setSessionVerified(verified);
      return verified;
    } catch (e) {
      setSessionVerified(false);
      return false;
    }
  }

  async isValid(token: string): Promise<boolean> {
    try {
      const resp = await this.authClient.sessionStatus(token);
      const isVerified = resp.state === 'verified';
      setSessionVerified(isVerified);
      return isVerified;
    } catch (e) {
      setSessionVerified(false);
      return false;
    }
  }
}
